import React, { useState } from "react";
import Cookies from "js-cookie";
import axios from "axios";
import { ExcelUploader } from "./ExcelUploader";
import { UploadResult, UserTypeFilter, UploadFile } from "./upload.types";
import { ArrowLeft, Zap, User, Users, Upload, Loader2, CheckCircle } from "lucide-react";
import { useEnergyAnalysisStore } from "../../store/EnergyStackStore";

// CONSTANTS
const UPLOAD_DATA_COOKIE = 'energy_upload_response_data';
const API_URL = "https://gec-dev-backend.energymarketplace.in/meter/api/v1/energy-load-surveys/submit";

function UploadBills() {
    const [userType, setUserType] = useState<UserTypeFilter>('generator');
    const [isUploadingGlobal, setIsUploadingGlobal] = useState(false);
    const { consumerLoadProfile, plantGenerationProfile, setConsumerLoadProfile, setPlantGenerationProfile } = useEnergyAnalysisStore()
    console.log({ consumerLoadProfile, plantGenerationProfile });


    // State for 'Both' mode - Controlled files
    const [genFiles, setGenFiles] = useState<UploadFile[]>([]);
    const [conFiles, setConFiles] = useState<UploadFile[]>([]);

    const saveResponse = (type: 'generator' | 'consumer', data: any) => {
        try {
            console.log({ saveResponse: data, type });
            if (type == "consumer") {
                let extractedData: any = data.map((d: any) => d)
                let mergedData = Object.assign({}, ...extractedData);
                // console.log({ mergedData });
                setConsumerLoadProfile(mergedData)
            }
            else if (type == "generator") {
                let extractedData: any = data.map((d: any) => d)
                let mergedData = Object.assign({}, ...extractedData);
                // console.log({ mergedData });
                setPlantGenerationProfile(mergedData)
            }
            const existingCookie = Cookies.get(UPLOAD_DATA_COOKIE);
            const existing = existingCookie ? JSON.parse(existingCookie) : {};
            existing[type] = data;
            Cookies.set(UPLOAD_DATA_COOKIE, JSON.stringify(existing), { expires: 1 });
            console.log(`Saved ${type} to cookies`);
        } catch (e) { console.error("Cookie Error", e); }
    };

    // Generic Upload Logic for Parent
    const performUpload = async (
        files: UploadFile[],
        type: 'generator' | 'consumer',
        setFilesState: React.Dispatch<React.SetStateAction<UploadFile[]>>
    ) => {
        let results: any[] = []

        // 1. Mark files as uploading
        setFilesState(prev => prev.map(f => f.status === 'pending' ? { ...f, status: 'uploading' } : f));

        for (const fileObj of files) {
            // Skip files already done
            if (fileObj.status === 'success') continue;

            const formData = new FormData();
            formData.append('file', fileObj.file);
            formData.append('userType', type);

            try {
                const res = await axios.post(API_URL, formData).then(res => res);
                // console.log({ apiResp: res });
                if (res.data.success) {
                    results.push(res.data.data);
                    // Mark as success
                    setFilesState(prev => prev.map(f => f.id === fileObj.id ? { ...f, status: 'success', progress: 100 } : f));
                } else {
                    throw new Error(res.data.message || "Upload failed");
                }
            } catch (err: any) {
                const errMsg = err.response?.data?.message || err.message || "Error";
                setFilesState(prev => prev.map(f => f.id === fileObj.id ? { ...f, status: 'error', errorMessage: errMsg } : f));
            }
        }
        return results;
    };

    const handleUploadAll = async () => {
        // Only upload pending files
        const genPending = genFiles.some(f => f.status === 'pending');
        const conPending = conFiles.some(f => f.status === 'pending');

        if (!genPending && !conPending) return;

        setIsUploadingGlobal(true);

        try {
            // Upload Generator Files
            if (genFiles.length > 0) {
                const genResults = await performUpload(genFiles, 'generator', setGenFiles);
                // console.log({ genResults });

                if (genResults) saveResponse('generator', genResults);
            }

            // Upload Consumer Files
            if (conFiles.length > 0) {
                const conResults = await performUpload(conFiles, 'consumer', setConFiles);
                // console.log({ conResults });
                if (conResults) saveResponse('consumer', conResults);
            }

        } finally {
            setIsUploadingGlobal(false);
        }
    };

    // Calculate totals for Global Button
    const totalFiles = genFiles.length + conFiles.length;
    const totalPending = genFiles.filter(f => f.status === 'pending').length + conFiles.filter(f => f.status === 'pending').length;
    const allSuccess = totalFiles > 0 && totalPending === 0 && genFiles.every(f => f.status === 'success') && conFiles.every(f => f.status === 'success');

    return (
        <>
            <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
                <div className="container mx-auto px-4 py-4 flex justify-between">
                    <a href="/" className="flex items-center gap-2 text-gray-600 hover:text-emerald-600 transition-colors">
                        <ArrowLeft className="w-5 h-5" /> <span>Back to Home</span>
                    </a>
                </div>
            </header>

            <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-cyan-50 py-8 px-4">
                <div className="relative max-w-6xl mx-auto space-y-8">

                    <div className="text-center mb-8">
                        <h1 className="text-3xl md:text-4xl lg:text-5xl mb-4 bg-gradient-to-r from-emerald-600 to-cyan-600 bg-clip-text text-transparent font-bold">
                            Upload Energy Data
                        </h1>
                        <p className="text-lg text-gray-600">
                            Upload your energy load surveys (CSV/Excel)
                        </p>
                    </div>

                    {/* Filter Selection */}
                    <div className="bg-white/60 backdrop-blur-md p-1.5 rounded-xl border border-gray-200 shadow-sm max-w-[400px] mx-auto sticky top-24 z-40">
                        <div className="flex justify-center">
                            <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">

                                <button
                                    onClick={() => setUserType("generator")}
                                    className={`flex-shrink-0 flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-medium transition-all
          ${userType === "generator"
                                            ? "bg-white text-emerald-600 shadow-sm ring-1 ring-black/5"
                                            : "text-gray-500 hover:bg-white/50"
                                        }`}
                                >
                                    <Zap className="w-4 h-4" />
                                    Generator
                                </button>

                                <button
                                    onClick={() => setUserType("consumer")}
                                    className={`flex-shrink-0 flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-medium transition-all
          ${userType === "consumer"
                                            ? "bg-white text-emerald-600 shadow-sm ring-1 ring-black/5"
                                            : "text-gray-500 hover:bg-white/50"
                                        }`}
                                >
                                    <User className="w-4 h-4" />
                                    Consumer
                                </button>

                                <button
                                    onClick={() => setUserType("both")}
                                    className={`flex-shrink-0 flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-medium transition-all
          ${userType === "both"
                                            ? "bg-white text-emerald-600 shadow-sm ring-1 ring-black/5"
                                            : "text-gray-500 hover:bg-white/50"
                                        }`}
                                >
                                    <Users className="w-4 h-4" />
                                    Both
                                </button>

                            </div>
                        </div>
                    </div>

                    {/* Content Area */}
                    <div className="transition-all duration-500">
                        {userType === 'both' ? (
                            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Generator Section */}
                                    <div className="bg-emerald-50/30 p-4 rounded-2xl border border-emerald-100/60">
                                        <ExcelUploader
                                            title="Generator Upload"
                                            userType="generator"
                                            externalFiles={genFiles}
                                            setExternalFiles={setGenFiles}
                                            hideUploadButton={true}
                                            isParentUploading={isUploadingGlobal}
                                        />
                                    </div>

                                    {/* Consumer Section */}
                                    <div className="bg-blue-50/30 p-4 rounded-2xl border border-blue-100/60">
                                        <ExcelUploader
                                            title="Consumer Upload"
                                            userType="consumer"
                                            externalFiles={conFiles}
                                            setExternalFiles={setConFiles}
                                            hideUploadButton={true}
                                            isParentUploading={isUploadingGlobal}
                                        />
                                    </div>
                                </div>

                                {/* Global Upload Button */}
                                {(genFiles.length > 0 || conFiles.length > 0) && (
                                    <div className="flex justify-center pb-8 sticky bottom-4 z-50">
                                        <button
                                            onClick={handleUploadAll}
                                            disabled={totalPending === 0 || isUploadingGlobal}
                                            className={`
                                                group relative px-8 py-4 text-white rounded-2xl font-bold text-lg shadow-xl 
                                                hover:shadow-2xl hover:scale-105 transition-all duration-300 disabled:opacity-70 disabled:scale-100 disabled:shadow-none
                                                flex items-center gap-3 w-full max-w-md justify-center
                                                ${allSuccess
                                                    ? "bg-green-500 cursor-default"
                                                    : "bg-gradient-to-r from-emerald-600 to-cyan-600"
                                                }
                                            `}
                                        >
                                            {isUploadingGlobal ? (
                                                <>
                                                    <Loader2 className="w-6 h-6 animate-spin" />
                                                    Processing {totalPending} Files...
                                                </>
                                            ) : allSuccess ? (
                                                <>
                                                    <CheckCircle className="w-6 h-6" />
                                                    All Uploaded Successfully
                                                </>
                                            ) : (
                                                <>
                                                    <Upload className="w-6 h-6 group-hover:-translate-y-1 transition-transform" />
                                                    Upload All Files ({totalPending})
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            // SINGLE VIEW (Fallback to standard uncontrolled behavior)
                            <div className="max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
                                <ExcelUploader
                                    title={userType === 'generator' ? "Generator Upload" : "Consumer Upload"}
                                    uploadUrl={API_URL}
                                    userType={userType}
                                    // Note: We don't pass externalFiles here, letting component handle its own state
                                    onUploadComplete={(res) => saveResponse(userType, res && res?.length > 0 ? res.map((d: any) => d.data) && res.map((d: any) => d.data).length > 0 ? res.map((d: any) => d.data.data) : [] : [])}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default UploadBills;